import { AlertTriangle, Upload } from 'lucide-react'
import React, { DragEvent, useRef, useState } from 'react'

import { getPasteKeyHint } from '@/utils/platform'

interface FileDropZoneProps {
  onFileSelect: (file: File) => void
  accept?: string
  maxSize?: number // in bytes
  disabled?: boolean
  className?: string
}

const FileDropZone: React.FC<FileDropZoneProps> = ({
  onFileSelect,
  accept = '.xlsx,.xls',
  maxSize = 10 * 1024 * 1024, // 10MB default
  disabled = false,
  className = ''
}) => {
  const [isDragOver, setIsDragOver] = useState(false)
  const [error, setError] = useState<string>('')
  const fileInputRef = useRef<HTMLInputElement>(null)

  // 获取适当的粘贴快捷键提示
  const pasteKeyHint = getPasteKeyHint()

  const validateFile = (file: File): boolean => {
    setError('')

    // Check file type
    const allowedTypes = accept.split(',').map((type) => type.trim())
    const isValidType = allowedTypes.some((type) => {
      if (type.startsWith('.')) {
        return file.name.toLowerCase().endsWith(type.toLowerCase())
      }
      return file.type === type
    })

    if (!isValidType) {
      setError(`请选择有效的文件类型: ${accept}`)
      return false
    }

    // Check file size
    if (file.size > maxSize) {
      setError(`文件大小不能超过 ${(maxSize / 1024 / 1024).toFixed(1)}MB`)
      return false
    }

    return true
  }

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDragOver(false)

    if (disabled) return

    const files = Array.from(e.dataTransfer.files)
    if (files.length > 0) {
      const file = files[0]
      if (validateFile(file)) {
        onFileSelect(file)
      }
    }
  }

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    if (!disabled) {
      setIsDragOver(true)
    }
  }

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDragOver(false)
  }

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file && validateFile(file)) {
      onFileSelect(file)
    }
    // Reset input value to allow selecting the same file again
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleClick = () => {
    if (!disabled && fileInputRef.current) {
      fileInputRef.current.click()
    }
  }

  return (
    <div className={`relative ${className}`}>
      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={handleClick}
        tabIndex={0}
        className={`
          border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-all duration-200
          ${
            isDragOver
              ? 'border-blue-500 bg-blue-50'
              : error
              ? 'border-red-300 bg-red-50'
              : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
          }
          ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
        `}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={accept}
          onChange={handleFileInputChange}
          className="hidden"
          disabled={disabled}
        />

        <div className="flex flex-col items-center space-y-4">
          {/* Upload Icon */}
          <div
            className={`
            w-12 h-12 rounded-full flex items-center justify-center
            ${
              isDragOver
                ? 'bg-blue-100 text-blue-600'
                : error
                ? 'bg-red-100 text-red-600'
                : 'bg-gray-100 text-gray-600'
            }
          `}
          >
            {error ? (
              <AlertTriangle className="w-6 h-6" />
            ) : (
              <Upload className="w-6 h-6" />
            )}
          </div>

          {/* Main Text */}
          <div>
            <p
              className={`text-lg font-medium ${
                error ? 'text-red-600' : 'text-gray-900'
              }`}
            >
              {isDragOver
                ? '松开鼠标上传文件'
                : `拖拽文件到此处、点击选择或 ${pasteKeyHint} 粘贴`}
            </p>
            <p className="text-sm text-gray-500 mt-1">
              支持格式: {accept.replace(/\./g, '').toUpperCase()}
            </p>
            <p className="text-sm text-gray-500">
              最大文件大小: {(maxSize / 1024 / 1024).toFixed(1)}MB
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-md">
              {error}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default FileDropZone
